import axios from 'axios';

/**
 * Resolve the API base URL at runtime.
 * In development, use the env var directly (set per .env file).
 * In production, derive from the current hostname so a single frontend
 * build works across all subdomains (paperdigest.xyz, bio.paperdigest.xyz, etc.).
 */
function resolveApiBaseUrl() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_BASE_URL;
  }
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}/api`;
}

const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Paper API endpoints
export const paperApi = {
  fetchPapers: async (endpoint) => {
    try {
      const response = await api.get(`/papers/${endpoint}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching papers from ${endpoint}:`, error);
      throw error;
    }
  },

  fetchPapersByDateRange: async (startDate, endDate) => {
    try {
      const response = await api.get(`/papers/archive/${startDate}/${endDate}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching papers by date range:', error);
      throw error;
    }
  },

  fetchPapersToday: async () => {
    try {
      const response = await api.get('/papers/today');
      return response.data;
    } catch (error) {
      console.error('Error fetching papers today:', error);
      throw error;
    }
  },

  fetchPaperById: async (paperId) => {
    try {
      const response = await api.get(`/papers/fetch/${paperId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching paper:', error);
      throw error;
    }
  },

  lookupPaper: async (paperId) => {
    try {
      const response = await api.post('/papers/lookup', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error looking up paper:', error);
      throw error;
    }
  },

  fetchPapersByIds: async (paperIds) => {
    try {
      const papers = [];
      for (const paperId of paperIds) {
        const paper = await paperApi.fetchPaperById(paperId);
        papers.push(paper);
      }
      return papers;
    } catch (error) {
      console.error('Error fetching papers:', error);
      throw error;
    }
  }
};

// Auth API endpoints
export const authApi = {
  getUser: async () => {
    try {
      const response = await api.get('/auth/user');
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  signIn: async (method) => {
    try {
      const response = await api.post(`/auth/${method}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error signing in';
      console.error('Error signing in:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  },

  addFavoritePaper: async (paperId) => {
    try {
      const response = await api.post('/auth/add-favorite-paper', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error adding favorite paper:', error);
      throw error;
    }
  },

  removeFavoritePaper: async (paperId) => {
    try {
      const response = await api.post('/auth/remove-favorite-paper', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error removing favorite paper:', error);
      throw error;
    }
  },

  addFavoriteTag: async (tag) => {
    try {
      const response = await api.post('/auth/add-favorite-tag', { tag });
      return response.data;
    } catch (error) {
      console.error('Error adding favorite tag:', error);
      throw error;
    }
  },

  removeFavoriteTag: async (tag) => {
    try {
      const response = await api.post('/auth/remove-favorite-tag', { tag });
      return response.data;
    } catch (error) {
      console.error('Error removing favorite tag:', error);
      throw error;
    }
  },

  addAiSummarizedPaper: async (paperId) => {
    try {
      const response = await api.post('/auth/add-ai-summarized-paper', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error adding AI summarized paper:', error);
      throw error;
    }
  },

};


export const emailAuthApi = {
  requestEmailAuth: async (email) => {
    try {
      const response = await api.post('/auth/email-auth/request', { email });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send authentication code';
      console.error('Email auth request error:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  verifyEmailAuth: async (email, code) => {
    try {
      const response = await api.post('/auth/email-auth/verify', { email, code });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify code';
      console.error('Email auth verification error:', errorMessage);
      throw new Error(errorMessage);
    }
  }
};

// ---- SSE streaming helper ------------------------------------------------
//
// The AI summary and chat endpoints both speak text/event-stream over POST.
// The browser's built-in EventSource only supports GET, so we roll our own:
// fetch() gives us a ReadableStream, we decode + buffer on "\n\n" (the SSE
// record delimiter), then route each record's `event:` / `data:` lines to
// the matching handler.
//
// IMPORTANT: this function is intentionally NOT async. We need to return the
// AbortController synchronously so callers (useEffect cleanup in React 18
// strict mode in particular) can cancel the fetch the moment they decide to,
// not wait for the stream to finish. Kicking the work off in an IIFE gives
// us the sync return while still letting the inner body use await.

const SSE_BASE_URL = API_BASE_URL;

function postSse(path, body, handlers = {}) {
  const { onMeta, onChunk, onDone, onError, signal: externalSignal } = handlers;
  const controller = new AbortController();
  // Abort our internal controller if the caller also aborts externally.
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort());
  }

  const dispatch = (event, dataStr) => {
    let data;
    try {
      data = dataStr ? JSON.parse(dataStr) : null;
    } catch (_) {
      data = dataStr;
    }
    if (controller.signal.aborted) return;
    if (event === 'meta' && onMeta) onMeta(data);
    else if (event === 'chunk' && onChunk) onChunk(data);
    else if (event === 'done' && onDone) onDone(data);
    else if (event === 'error' && onError) onError(new Error(data?.message || 'stream error'));
  };

  (async () => {
    let response;
    try {
      response = await fetch(`${SSE_BASE_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        signal: controller.signal
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (onError) onError(err);
      return;
    }

    if (!response.ok) {
      // Try to pull a JSON error message; fall back to status text.
      let message = response.statusText || 'Request failed';
      try {
        const errBody = await response.json();
        if (errBody && errBody.message) message = errBody.message;
      } catch (_) {}
      if (!controller.signal.aborted && onError) onError(new Error(message));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const flushBuffered = () => {
      // SSE records are separated by a blank line (\n\n). Everything before
      // the delimiter is one record; multiple `data:` lines in a record are
      // concatenated with newlines per the spec.
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const rawRecord = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (!rawRecord.trim()) continue;
        let eventName = 'message';
        const dataLines = [];
        for (const line of rawRecord.split('\n')) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        dispatch(eventName, dataLines.join('\n'));
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        flushBuffered();
      }
      // Flush any trailing record that wasn't terminated by \n\n.
      buffer += decoder.decode();
      if (buffer.trim()) {
        buffer += '\n\n';
        flushBuffered();
      }
    } catch (err) {
      if (err.name !== 'AbortError' && !controller.signal.aborted && onError) {
        onError(err);
      }
    }
  })();

  return controller;
}

// AI API endpoints
export const aiApi = {
  // Legacy JSON call, kept until all UI call sites have migrated to
  // streamSummary. Remove once PaperCard/PaperInformation use the stream.
  generateSummary: async (paperId) => {
    try {
      const response = await api.post('/ai/summary', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error generating AI summary:', error);
      throw error;
    }
  },

  // Streaming entry point. Returns an AbortController so a useEffect cleanup
  // can cancel a fetch that's still running when the component unmounts.
  // Handlers: { onMeta, onChunk, onDone, onError }
  streamSummary: (paperId, handlers) =>
    postSse('/ai/summary', { paperId }, handlers),

  // Lists every paper the current user has generated an AI summary for.
  // Powers the AI Dashboard's "Summaries" tab.
  listSummaries: async () => {
    try {
      const response = await api.get('/ai/summaries');
      return response.data;
    } catch (error) {
      console.error('Error listing AI summaries:', error);
      throw error;
    }
  }
};

// Chat-with-paper API
export const chatApi = {
  createOrGetSession: async (paperId) => {
    try {
      const response = await api.post('/ai/chat/sessions', { paperId });
      return response.data;
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  },

  // Lists every chat session the current user has opened, with paper
  // metadata and last-message preview. Powers the AI Dashboard's "Chats" tab.
  listSessions: async () => {
    try {
      const response = await api.get('/ai/chat/sessions');
      return response.data;
    } catch (error) {
      console.error('Error listing chat sessions:', error);
      throw error;
    }
  },

  listMessages: async (sessionId) => {
    try {
      const response = await api.get(`/ai/chat/sessions/${sessionId}/messages`);
      return response.data;
    } catch (error) {
      console.error('Error listing chat messages:', error);
      throw error;
    }
  },

  streamMessage: (sessionId, content, handlers) =>
    postSse(`/ai/chat/sessions/${sessionId}/messages`, { content }, handlers)
};

// Subscription API endpoints
export const subscriptionApi = {

  getProducts: async () => {
    try {
      const response = await api.get('/subscription/products');
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  createCheckoutSession: async (product) => {
    try {
      const response = await api.post('/subscription/create-checkout-session', { product });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  createPortalSession: async () => {
    try {
      const response = await api.post('/subscription/create-portal-session');
      return response.data;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  syncWithStripe: async () => {
    try {
      const response = await api.post('/subscription/sync-status');
      return response.data;
    } catch (error) {
      console.error('Error syncing subscription status:', error);
      throw error;
    }
  },

  checkoutSuccess: async (sessionId) => {
    try {
      const response = await api.get(`/subscription/checkout-success?session_id=${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error handling payment:', error);
      throw error;
    }
  },

  getActiveEntitlements: async () => {
    try {
      const response = await api.get('/subscription/active-entitlements');
      return response.data;
    } catch (error) {
      console.error('Error fetching active entitlements:', error);
      throw error;
    }
  }

};

export const statsApi = {
  getGeneralStats: async () => {
    try {
      const response = await api.get('/stats/general');
      return response.data;
    } catch (error) {
      console.error('Error fetching general statistics:', error);
      throw error;
    }
  },

  getHotPapers: async () => {
    try {
      const response = await api.get('/stats/hot-papers');
      return response.data;
    } catch (error) {
      console.error('Error fetching hot papers:', error);
      throw error;
    }
  }
};
